/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react"
import { Controller, useForm } from 'react-hook-form'
import {
  doc,
  collection,
  DocumentSnapshot,
  addDoc,
  updateDoc,
  onSnapshot,
  getDocs,
  query,
  where
} from 'firebase/firestore'
import { db } from '../../../firebase'
import {
  Box,
  Button,
  Typography,
  Backdrop,
  CircularProgress,
  Modal,
  Stack,
  FormControl,
  Container,
  TextField,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormLabel,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText
} from "@mui/material"
import dayjs, { Dayjs } from "dayjs"
import { toast } from 'react-toastify'
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"

type Props = {
  productId?: string
  isOpen: boolean
  onClose: () => void
}

const defaultValues = () => {
  const today = dayjs()
  return {
    processingDate: today,
    type: "エゾシカ",
    weight: null,
    storageType: "冷凍",
    expirationDate: today.add(1, 'year').subtract(1, 'day'),
    memo: ""
  }
}

const MinceProductForm = (props: Props) => {
  const { productId, isOpen, onClose } = props
  const { control, handleSubmit, reset, getValues } = useForm<Record<string, any>>({ defaultValues: defaultValues() })
  const [prosessing, setProcessing] = useState<boolean>(false)
  const productsRef = collection(db, 'products')

  useEffect(() => {
    if (!isOpen) return
    if (!productId) {
      reset(defaultValues())
      return
    }
    const unsubscribe = onSnapshot(doc(productsRef, productId), (snap: DocumentSnapshot) => {
      if (snap.exists()) {
        const data = snap.data()
        reset({
          processingDate: dayjs(data.processingDate),
          type: data.type,
          weight: parseInt(data.weight, 10),
          storageType: data.storageType || "冷凍",
          expirationDate: dayjs(data.expirationDate),
          memo: data.memo ?? ""
        })
      }
    })
    return unsubscribe
  }, [productId, isOpen])

  const calculateExpirationDate = (processingDate: Dayjs, storageType: string) => {
    if (storageType === "チルド") return processingDate.add(14, 'day')
    return processingDate.add(1, 'year').subtract(1, 'day')
  }

  const onChangeProcessingDate = (v: Dayjs | null) => {
    if (!v) return
    const current = getValues()
    const expirationDate = calculateExpirationDate(v, current.storageType || "冷凍")
    reset({ ...current, processingDate: v, expirationDate })
  }

  const onChangeStorageType = (storageType: string) => {
    const current = getValues()
    const processingDate = current.processingDate || dayjs()
    const expirationDate = calculateExpirationDate(processingDate, storageType)
    reset({ ...current, storageType, expirationDate })
  }

  const generateProductNo = async (processingDate: Dayjs): Promise<string> => {
    const dateStr = processingDate.format("YYYYMMDD")
    const prefix = `MINC-${dateStr}-`
    const snap = await getDocs(query(productsRef, where('isMinced', '==', true)))
    const maxSeq = snap.docs.reduce((max, d) => {
      const no: string = d.data().productNo || ''
      if (!no.startsWith(prefix)) return max
      const seq = parseInt(no.substring(prefix.length))
      return Math.max(max, isNaN(seq) ? 0 : seq)
    }, 0)
    return `${prefix}${('000' + (maxSeq + 1)).slice(-3)}`
  }

  const submit = async ({ processingDate, type, weight, storageType, expirationDate, memo }: Record<string, any>) => {
    setProcessing(true)
    try {
      const updateParams = {
        processingDate: processingDate.format("YYYY/MM/DD"),
        type,
        part: "ミンチ",
        weight: parseInt(weight, 10),
        storageType,
        expirationDate: expirationDate.format("YYYY/MM/DD"),
        memo: memo ?? "",
        isSold: false,
        isMinced: true
      }
      if (productId) {
        await updateDoc(doc(productsRef, productId), { ...updateParams, updatedAt: dayjs().format('YYYY/MM/DD HH:mm') })
      } else {
        const productNo = await generateProductNo(processingDate)
        await addDoc(productsRef, { ...updateParams, productNo, createdAt: dayjs().format('YYYY/MM/DD HH:mm') })
      }
      toast.success("ミンチ製品情報を登録しました")
    } catch (e: any) {
      console.error('MinceProductForm submit error:', e)
      toast.error(e?.message ?? JSON.stringify(e))
    } finally {
      setProcessing(false)
      onClose()
    }
  }

  return (
    <Modal
      open={isOpen}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClose={onClose}
      sx={{ padding: 2 }}
    >
      <Container maxWidth="xs" sx={{ mt: 6 }}>
        <Box
          component="form"
          onSubmit={handleSubmit(submit)}
          display="flex-col"
          justifyContent="center"
          alignItems="center"
          bgcolor="white"
        >
          <Box mx={2} width="50vh" display="flex-col">
            <Box display="flex" justifyContent="start" m={1}>
              <Typography variant="h5" mt={1}>ミンチ製品登録</Typography>
            </Box>
          </Box>
          <Stack spacing={2} border={1} borderRadius={1} borderColor="#333" p={2} mx={2}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
              <Controller
                name="processingDate"
                control={control}
                rules={{
                  validate: (value) => {
                    if (!value) return '加工日の入力は必須です'
                    if (!dayjs(value).isValid()) return '日付形式が間違っています'
                  }
                }}
                render={({ field, fieldState: { error } }) => (
                  <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ja">
                    <DatePicker
                      {...field}
                      label="加工日"
                      format="YYYY/MM/DD"
                      slotProps={{
                        textField: { error: !!error, helperText: error?.message }
                      }}
                      onChange={(v) => onChangeProcessingDate(v)}
                    />
                  </LocalizationProvider>
                )}
              />
              <Controller
                name="storageType"
                control={control}
                render={({ field }) => (
                  <FormControl component="fieldset">
                    <FormLabel component="legend">保存方法</FormLabel>
                    <RadioGroup
                      {...field}
                      row
                      onChange={(e) => {
                        field.onChange(e.target.value)
                        onChangeStorageType(e.target.value)
                      }}
                    >
                      <FormControlLabel value="冷凍" control={<Radio />} label="冷凍" />
                      <FormControlLabel value="チルド" control={<Radio />} label="チルド" />
                    </RadioGroup>
                  </FormControl>
                )}
              />
            </Box>
            <Box display="flex" justifyContent="center">
              <Controller
                name="type"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <FormControl fullWidth error={!!error}>
                    <InputLabel id="type-select-label">獣種</InputLabel>
                    <Select
                      labelId="type-select-label"
                      label="獣種"
                      {...field}
                    >
                      <MenuItem value="エゾシカ">エゾシカ</MenuItem>
                      <MenuItem value="ヒグマ">ヒグマ</MenuItem>
                    </Select>
                    <FormHelperText>{error?.message || ""}</FormHelperText>
                  </FormControl>
                )}
              />
            </Box>
            <Box display="flex" justifyContent="center">
              <Controller
                name="weight"
                control={control}
                rules={{ required: { value: true, message: '重量の入力は必須です' } }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    type="number"
                    label="重量(g)"
                    fullWidth
                    placeholder="999"
                    error={!!error}
                    helperText={error && error.message}
                  />
                )}
              />
            </Box>
            <Box display="flex" justifyContent="start">
              <Controller
                name="expirationDate"
                control={control}
                rules={{
                  validate: (value) => {
                    if (!value) return '消費期限の入力は必須です'
                    if (!dayjs(value).isValid()) return '日付形式が間違っています'
                  }
                }}
                render={({ field, fieldState: { error } }) => (
                  <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ja">
                    <DatePicker
                      {...field}
                      label="消費期限"
                      format="YYYY/MM/DD"
                      slotProps={{
                        textField: { error: !!error, helperText: error?.message }
                      }}
                    />
                  </LocalizationProvider>
                )}
              />
            </Box>
            <Box display="flex" justifyContent="center">
              <Controller
                name="memo"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    multiline
                    label="備考"
                    fullWidth
                    placeholder="備考"
                  />
                )}
              />
            </Box>
          </Stack>
          <Box m={1} p={1} display="flex" justifyContent="center">
            <Button variant="outlined" color="warning" onClick={onClose}>キャンセル</Button>
            <Button variant="contained" color="info" onClick={handleSubmit(submit)} sx={{ ml: 2 }}>登録</Button>
          </Box>
          <Backdrop
            sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
            open={prosessing}
            onClick={() => setProcessing(false)}
          >
            <CircularProgress color="inherit" />
          </Backdrop>
        </Box>
      </Container>
    </Modal>
  )
}

export default MinceProductForm
